import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Loader2,
  AlertCircle,
  Play,
  ExternalLink,
  GitPullRequest,
  GitMerge,
  CircleDot,
  Ban,
  Info,
  Package,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Tag,
  BookOpen,
  MessageSquareText,
  Layers,
  GitBranch,
  Upload,
  CheckCircle2,
} from 'lucide-react';
import { useAppAuth } from '../../contexts/AuthContext';
import { useTeamsConfig } from '../../hooks/useJira';
import { useReleaseReadiness, useReleaseReadinessSprints, usePublishReleaseReadiness } from '../../hooks/useReleaseReadiness';

// Ordered pipeline states + display metadata (matches backend STATE_ORDER)
const STATES = [
  { key: 'merged-release', label: 'Merged into release', color: 'green', icon: GitMerge },
  { key: 'open-release', label: 'Open PR → release', color: 'blue', icon: GitPullRequest },
  { key: 'merged-main', label: 'Merged to develop only', color: 'amber', icon: GitMerge },
  { key: 'open-main', label: 'Open PR → develop', color: 'amber', icon: GitPullRequest },
  { key: 'no-pr', label: 'No PR found', color: 'gray', icon: CircleDot },
  { key: 'no-pr-expected', label: 'No PR (expected)', color: 'slate', icon: CircleDot },
  { key: 'abandoned', label: 'Abandoned', color: 'gray', icon: Ban },
  { key: 'orphan', label: 'Orphan / no story', color: 'red', icon: AlertCircle },
];

const STATE_MAP = STATES.reduce((acc, s) => {
  acc[s.key] = s;
  return acc;
}, {});

const STATE_INDEX = STATES.reduce((acc, s, i) => {
  acc[s.key] = i;
  return acc;
}, {});

// Sortable table columns with value accessors (null-safe)
const COLUMNS = [
  { key: 'key', label: 'Key', get: (s) => s.key || '' },
  { key: 'summary', label: 'Summary', get: (s) => s.summary || '' },
  { key: 'repos', label: 'Repo', get: (s) => (s.repos || []).join(', ') },
  { key: 'assignee', label: 'Assignee', get: (s) => s.assignee || '' },
  { key: 'developPRs', label: 'Develop PR', get: (s) => (s.developPRs?.[0]?.number ?? -1) },
  { key: 'releasePRs', label: 'Release PR', get: (s) => (s.releasePRs?.[0]?.number ?? -1) },
  { key: 'mergedAt', label: 'Merged Date', get: (s) => (s.mergedAt ? new Date(s.mergedAt).getTime() : 0) },
  { key: 'state', label: 'State', get: (s) => STATE_INDEX[s.state] ?? 99 },
];

const STAT_TILE = {
  green: 'bg-green-50 border-green-200 text-green-700',
  blue: 'bg-blue-50 border-blue-200 text-blue-700',
  amber: 'bg-amber-50 border-amber-200 text-amber-700',
  gray: 'bg-gray-50 border-gray-200 text-gray-600',
  slate: 'bg-slate-50 border-slate-200 text-slate-600',
  red: 'bg-red-50 border-red-200 text-red-700',
};

const STATE_PILL = {
  green: 'bg-green-100 text-green-700',
  blue: 'bg-blue-100 text-blue-700',
  amber: 'bg-amber-100 text-amber-700',
  gray: 'bg-gray-100 text-gray-600',
  slate: 'bg-slate-100 text-slate-600',
  red: 'bg-red-100 text-red-700',
};

const ROW_TINT = {
  'merged-release': 'bg-green-100/70',
  'open-release': 'bg-blue-100/50',
  'merged-main': 'bg-amber-100/80',
  'open-main': 'bg-amber-100/50',
  'no-pr': '',
  'no-pr-expected': 'bg-slate-100/70',
  abandoned: 'bg-gray-100/80',
  orphan: 'bg-red-100/60',
};

// Fallback list used only until /api/jira/teams responds (or if it fails) - the real options come
// from the same TEAMS config the backend uses, so a new board doesn't need editing in two places.
const FALLBACK_PROJECT_OPTIONS = ['ADM', 'PLT', 'CPT', 'SSM', 'SFMC', 'MNS', 'DIS'];
const JIRA_BASE = 'https://sunnyrewards.atlassian.net/browse';

const ORPHAN_REASON_LABEL = {
  'not-in-fix-version': 'Not in this Fix Version',
  'key-not-found': 'Jira key not found',
  'no-key': 'No Jira key referenced',
};

function OrphanBadge({ story }) {
  if (!story.isOrphan) return null;
  const label = ORPHAN_REASON_LABEL[story.orphanReason] || 'Orphan';
  const title =
    story.orphanReason === 'not-in-fix-version'
      ? `Fix Version(s) on this story: ${story.fixVersions?.length ? story.fixVersions.join(', ') : 'none set'}`
      : label;
  return (
    <span
      className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-red-100 text-red-700 align-middle whitespace-nowrap"
      title={title}
    >
      <AlertCircle className="w-2.5 h-2.5" />
      {label}
    </span>
  );
}

// Flags a row annotated via the Confluence "Release Exceptions" table (see confluenceService's
// parseExceptions on the backend) - hovering shows the human-entered decision + reason.
function ExceptionBadge({ story }) {
  if (!story.exception) return null;
  const { decision, reason } = story.exception;
  const title = [decision, reason].filter(Boolean).join(' — ') || 'Documented exception';
  return (
    <span
      className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-600 align-middle whitespace-nowrap"
      title={title}
    >
      <MessageSquareText className="w-2.5 h-2.5" />
      {decision || 'Exception'}
    </span>
  );
}

// Flags a story tagged to an earlier release that was deliberately merged into this (later)
// release branch to avoid orphaning it on the earlier branch. It's already tagged appropriately
// (just to an earlier Fix Version), so it's shown as informational rather than a red orphan.
function CarriedForwardBadge({ story }) {
  if (!story.carriedForward) return null;
  const versions = story.fixVersions?.length ? story.fixVersions.join(', ') : 'an earlier release';
  return (
    <span
      className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-teal-100 text-teal-700 align-middle whitespace-nowrap"
      title={`Carried forward from an earlier release — tagged to Fix Version(s): ${versions}. Merged into this release branch to avoid orphaning it on the earlier branch.`}
    >
      <GitBranch className="w-2.5 h-2.5" />
      Carried forward
    </span>
  );
}

// Flags a row that was pulled in via the optional sprint-inclusion toggle rather than the
// fixVersion scope - i.e. it's on the selected sprint but not (yet) tagged with this Fix Version.
function SprintBadge({ story }) {
  if (!story.fromSprint) return null;
  return (
    <span
      className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-purple-100 text-purple-700 align-middle whitespace-nowrap"
      title="Included via the sprint toggle — on the selected sprint but not tagged with this Fix Version"
    >
      <Layers className="w-2.5 h-2.5" />
      Sprint
    </span>
  );
}

function StatePill({ state }) {
  const meta = STATE_MAP[state] || { label: state, color: 'gray' };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${STATE_PILL[meta.color]}`}>
      {meta.label}
    </span>
  );
}

function StatTile({ state, value, active, onClick }) {
  const Icon = state.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left rounded-lg border p-4 shadow-sm transition-all ${STAT_TILE[state.color]} ${
        active ? 'ring-2 ring-offset-1 ring-amber-400' : 'hover:shadow-md'
      }`}
    >
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4" />
        <span className="text-xs font-medium">{state.label}</span>
      </div>
      <p className="text-2xl font-bold mt-2 text-gray-900">{value}</p>
    </button>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch {
    return iso;
  }
}

// Renders a stack of PR links for a single table cell — a story can have more than one
// PR against the same target (e.g. one per affected repo), so this is a list, not a single value.
function PRBadgeList({ prs }) {
  if (!prs || prs.length === 0) return <span className="text-gray-400">—</span>;
  return (
    <div className="flex flex-col gap-1">
      {prs.map((pr) => {
        const Icon = pr.mergedAt ? GitMerge : pr.state === 'open' ? GitPullRequest : Ban;
        const statusText = pr.mergedAt ? `merged ${formatDate(pr.mergedAt)}` : pr.state === 'open' ? 'open' : 'closed';
        return (
          <a
            key={`${pr.repo}-${pr.number}`}
            href={pr.url}
            target="_blank"
            rel="noopener noreferrer"
            title={`${pr.repo} #${pr.number} · ${statusText}`}
            className="text-amber-600 hover:text-amber-700 inline-flex items-center gap-1 text-xs whitespace-nowrap"
          >
            <Icon className="w-3 h-3 flex-shrink-0" />
            #{pr.number}
            <span className="text-gray-400">({pr.repo})</span>
          </a>
        );
      })}
    </div>
  );
}

export default function ReleaseReadiness() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Form state seeded from URL query params (shareable links)
  const [fixVersionInput, setFixVersionInput] = useState(searchParams.get('fixVersion') || '');
  const [releaseBranchInput, setReleaseBranchInput] = useState(searchParams.get('releaseBranch') || '');
  const [projectInput, setProjectInput] = useState(searchParams.get('projectKey') || 'ADM');
  // Optional sprint inclusion (default off) — on when a sprintId is present in the URL
  const [includeSprint, setIncludeSprint] = useState(!!searchParams.get('sprintId'));
  const [sprintInput, setSprintInput] = useState(searchParams.get('sprintId') || '');

  // Committed values (what the query actually runs against) come from the URL so links auto-run
  const activeFixVersion = searchParams.get('fixVersion') || '';
  const activeReleaseBranch = searchParams.get('releaseBranch') || '';
  const activeProject = searchParams.get('projectKey') || 'ADM';
  const activeSprintId = searchParams.get('sprintId') || '';

  const [activeFilter, setActiveFilter] = useState('all');
  // Default sort by State descending so "Merged into release" sits at the bottom of the list
  const [sort, setSort] = useState({ column: 'state', direction: 'desc' });

  const { data, isLoading, isFetching, isError, error, refetch } = useReleaseReadiness(
    activeFixVersion,
    activeReleaseBranch,
    activeProject,
    activeSprintId || null,
  );

  // Sprint list for the picker — only fetched once the toggle is on, scoped to the chosen project
  const { data: sprintsData, isFetching: sprintsFetching } = useReleaseReadinessSprints(
    projectInput,
    includeSprint,
  );
  const availableSprints = useMemo(() => sprintsData?.data || [], [sprintsData]);

  // Project options come from the backend's own TEAMS config rather than a duplicated constant
  const { data: teamsData } = useTeamsConfig();
  const projectOptions = useMemo(() => {
    const teams = Array.isArray(teamsData?.data) ? teamsData.data : [];
    const keys = [...new Set(teams.map((t) => t.projectKey).filter(Boolean))].sort();
    return keys.length > 0 ? keys : FALLBACK_PROJECT_OPTIONS;
  }, [teamsData]);

  // Publishing the snapshot to Confluence is an explicit, permission-gated action
  const { hasPermission } = useAppAuth();
  const canPublish = hasPermission('release-readiness:publish');
  const publishMutation = usePublishReleaseReadiness();

  // Once sprints load (toggle on) and nothing is selected yet, default to the active sprint
  useEffect(() => {
    if (includeSprint && !sprintInput && availableSprints.length > 0) {
      const activeSprint = availableSprints.find((s) => s.state === 'active') || availableSprints[0];
      if (activeSprint) setSprintInput(String(activeSprint.id));
    }
  }, [includeSprint, sprintInput, availableSprints]);

  // Keep inputs in sync if the URL changes externally (e.g. back/forward navigation)
  useEffect(() => {
    setFixVersionInput(searchParams.get('fixVersion') || '');
    setReleaseBranchInput(searchParams.get('releaseBranch') || '');
    setProjectInput(searchParams.get('projectKey') || 'ADM');
    const urlSprintId = searchParams.get('sprintId') || '';
    setSprintInput(urlSprintId);
    setIncludeSprint(!!urlSprintId);
  }, [searchParams]);

  const handleRun = (e) => {
    e?.preventDefault?.();
    const fv = fixVersionInput.trim();
    const rb = releaseBranchInput.trim();
    if (!fv || !rb) return;
    // sprintId is only committed when the toggle is on AND a sprint is picked
    const sprintId = includeSprint && sprintInput ? String(sprintInput) : '';
    const unchanged =
      fv === activeFixVersion &&
      rb === activeReleaseBranch &&
      projectInput === activeProject &&
      sprintId === activeSprintId;
    if (unchanged) {
      // Same query as before: force a fresh fetch so "Run" always refreshes the screen
      refetch();
    } else {
      const params = { fixVersion: fv, releaseBranch: rb, projectKey: projectInput };
      if (sprintId) params.sprintId = sprintId;
      setSearchParams(params);
    }
  };

  const report = data?.data;
  const counts = report?.counts || {};
  const backendErrors = report?.errors || null;

  // Publish feedback belongs to one specific report - drop it as soon as a different one is being
  // viewed, so "Published 10:42" can't linger over a freshly-run, unpublished release.
  const resetPublishState = publishMutation.reset;
  useEffect(() => {
    resetPublishState();
  }, [resetPublishState, activeFixVersion, activeReleaseBranch, activeProject, activeSprintId]);

  const handlePublish = () => {
    if (!activeFixVersion || !activeReleaseBranch) return;
    publishMutation.mutate({
      fixVersion: activeFixVersion,
      releaseBranch: activeReleaseBranch,
      projectKey: activeProject,
      sprintId: activeSprintId || null,
    });
  };

  // A publish is only offered against a report that's actually publishable: the fix version has to
  // exist in Jira, and Jira/GitHub both have to have answered cleanly this run (the server enforces
  // both - this just avoids offering a button that would be refused).
  const publishBlockedReason =
    report?.confluence?.publishEnabled === false
      ? 'Publishing is disabled in this environment.'
      : report?.fixVersionKnown === false
        ? `Jira has no fix version "${activeFixVersion}" on ${activeProject}.`
        : backendErrors?.jira || backendErrors?.github
          ? 'Jira or GitHub failed this run, so the report is incomplete.'
          : null;

  const stories = useMemo(() => report?.stories || [], [report]);

  const filteredStories = useMemo(() => {
    let rows = stories;
    if (activeFilter !== 'all') rows = rows.filter((s) => s.state === activeFilter);

    const col = COLUMNS.find((c) => c.key === sort.column);
    if (col) {
      const dir = sort.direction === 'asc' ? 1 : -1;
      rows = [...rows].sort((a, b) => {
        const av = col.get(a);
        const bv = col.get(b);
        if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
        return String(av).localeCompare(String(bv), undefined, { numeric: true }) * dir;
      });
    }
    return rows;
  }, [stories, activeFilter, sort]);

  const toggleSort = (columnKey) => {
    setSort((prev) =>
      prev.column === columnKey
        ? { column: columnKey, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { column: columnKey, direction: 'asc' },
    );
  };

  const hasQuery = !!activeFixVersion && !!activeReleaseBranch;
  const showCherryPickCallout = (counts['merged-main'] || 0) > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2">
          <Package className="w-6 h-6 text-amber-500" />
          <h1 className="text-2xl font-bold text-gray-900">Release Readiness</h1>
        </div>
        <p className="text-gray-500 mt-1">
          Cross-reference a Jira fix version against GitHub PRs to see where each story sits in the release pipeline.
        </p>
      </div>

      {/* Input bar */}
      <form
        onSubmit={handleRun}
        className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm flex flex-wrap items-end gap-3"
      >
        <div className="flex flex-col">
          <label className="text-xs font-medium text-gray-500 mb-1">Fix Version</label>
          <input
            type="text"
            value={fixVersionInput}
            onChange={(e) => setFixVersionInput(e.target.value)}
            placeholder="ADM.2026.2.4.0"
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 w-56"
          />
        </div>
        <div className="flex flex-col">
          <label className="text-xs font-medium text-gray-500 mb-1">Release Branch</label>
          <input
            type="text"
            value={releaseBranchInput}
            onChange={(e) => setReleaseBranchInput(e.target.value)}
            placeholder="release/2026.2.4.0"
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 w-56"
          />
        </div>
        <div className="flex flex-col">
          <label className="text-xs font-medium text-gray-500 mb-1">Project</label>
          <select
            value={projectInput}
            onChange={(e) => {
              setProjectInput(e.target.value);
              // Sprints are board/project-specific — clear the selection when the project changes
              setSprintInput('');
            }}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
          >
            {projectOptions.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>

        {/* Optional sprint inclusion (default off) */}
        <div className="flex flex-col">
          <label className="text-xs font-medium text-gray-500 mb-1">Include Sprint</label>
          <label className="inline-flex items-center gap-2 h-[42px] px-1 cursor-pointer select-none" title="Also include every story on a selected sprint, even if it isn't tagged with this Fix Version">
            <input
              type="checkbox"
              checked={includeSprint}
              onChange={(e) => {
                setIncludeSprint(e.target.checked);
                if (!e.target.checked) setSprintInput('');
              }}
              className="w-4 h-4 rounded border-gray-300 text-amber-500 focus:ring-amber-400"
            />
            <span className="inline-flex items-center gap-1 text-sm text-gray-700">
              <Layers className="w-4 h-4 text-purple-500" />
              Sprint stories
            </span>
          </label>
        </div>

        {includeSprint && (
          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1">Sprint</label>
            <select
              value={sprintInput}
              onChange={(e) => setSprintInput(e.target.value)}
              disabled={sprintsFetching}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white w-64 disabled:opacity-50"
            >
              {sprintsFetching && <option value="">Loading sprints…</option>}
              {!sprintsFetching && availableSprints.length === 0 && <option value="">No sprints found</option>}
              {!sprintsFetching &&
                availableSprints.map((s) => (
                  <option key={s.id} value={s.id}>
                    {/* Closed is the norm here, so only a non-closed state is worth calling out -
                        and rendering whatever state the API returned avoids a branch per value. */}
                    {s.name}{s.state && s.state !== 'closed' ? ` (${s.state})` : ''}
                  </option>
                ))}
            </select>
          </div>
        )}

        <button
          type="submit"
          disabled={!fixVersionInput.trim() || !releaseBranchInput.trim() || (includeSprint && !sprintInput)}
          className="px-4 py-2 bg-amber-400 text-gray-900 rounded-lg font-medium hover:bg-amber-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {isFetching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          Run
        </button>
      </form>

      {/* Empty prompt (no query yet) */}
      {!hasQuery && (
        <div className="bg-white rounded-lg border border-gray-200 p-10 text-center text-gray-500">
          <Package className="w-10 h-10 mx-auto text-gray-300 mb-3" />
          <p className="font-medium text-gray-600">Enter a fix version and release branch to run the report.</p>
          <p className="text-sm mt-1">e.g. <code className="bg-gray-100 px-1.5 py-0.5 rounded">ADM.2026.2.4.0</code> and <code className="bg-gray-100 px-1.5 py-0.5 rounded">release/2026.2.4.0</code></p>
        </div>
      )}

      {/* Loading */}
      {hasQuery && isLoading && (
        <div className="flex items-center justify-center min-h-[40vh]">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="w-12 h-12 text-amber-500 animate-spin" />
            <p className="text-gray-500">Querying Jira and GitHub…</p>
          </div>
        </div>
      )}

      {/* Top-level error (e.g. 503 GitHub token missing, 400) */}
      {hasQuery && isError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-red-800">Failed to load release readiness</p>
            <p className="text-sm text-red-600 mt-1">{error?.message}</p>
          </div>
        </div>
      )}

      {/* Results */}
      {hasQuery && !isLoading && !isError && report && (
        <>
          {(report.confluence?.pageUrl || canPublish) && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            {/* Confluence release page link - only present once the page has actually been created
                (first successful publish). In CONFLUENCE_LOCAL_MODE (default for local dev) this
                points at a gitignored local file rather than a real Confluence page, so it's shown
                as plain text instead of a clickable link. */}
            {report.confluence?.pageUrl && (
              report.confluence.pageUrl.startsWith('file://') ? (
                <p className="inline-flex items-center gap-1.5 text-sm text-gray-500" title={report.confluence.pageUrl}>
                  <BookOpen className="w-4 h-4" />
                  Local Confluence snapshot: <code className="bg-gray-100 px-1 rounded">{report.confluence.pageUrl.replace('file://', '')}</code>
                </p>
              ) : (
                <a
                  href={report.confluence.pageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-amber-600 hover:text-amber-700 font-medium"
                >
                  <BookOpen className="w-4 h-4" />
                  View release page in Confluence
                  <ExternalLink className="w-3 h-3" />
                </a>
              )
            )}

            {/* Publishing is an explicit action, never a side effect of viewing the report: it
                creates and updates a real page in the release's Confluence space. Hidden entirely
                without the release-readiness:publish permission (the API enforces it too). */}
            {canPublish && (
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={handlePublish}
                  disabled={publishMutation.isPending || !!publishBlockedReason}
                  title={publishBlockedReason || 'Write this report\'s snapshot to the release page in Confluence'}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {publishMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  Publish to Confluence
                </button>

                {publishBlockedReason && <span className="text-xs text-gray-500">{publishBlockedReason}</span>}

                {publishMutation.isError && (
                  <span className="inline-flex items-center gap-1 text-xs text-red-600">
                    <AlertCircle className="w-3.5 h-3.5" />
                    {publishMutation.error?.message || 'Publish failed'}
                  </span>
                )}

                {/* Three distinct non-error outcomes: refused because the report was incomplete,
                    written, or skipped because the page already held this exact snapshot. */}
                {publishMutation.isSuccess && !publishMutation.isPending && (
                  <span className="inline-flex items-center gap-1 text-xs text-gray-600">
                    {report.confluence?.skipped ? (
                      <>
                        <AlertCircle className="w-3.5 h-3.5 text-amber-500" />
                        {report.confluence.skipReason}
                      </>
                    ) : report.confluence?.published ? (
                      <>
                        <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                        Published{report.confluence?.publishedAt ? ` ${formatDateTime(report.confluence.publishedAt)}` : ''}
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-3.5 h-3.5 text-gray-400" />
                        Already up to date — page not modified
                      </>
                    )}
                  </span>
                )}
              </div>
            )}
          </div>
          )}

          {/* Unknown fix version - a typo satisfies the JQL and just returns zero rows, which
              otherwise reads exactly like "nothing tagged to this release yet". */}
          {report.fixVersionKnown === false && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-amber-800">Fix version not found in Jira</p>
                <p className="text-sm text-amber-700 mt-1">
                  <code className="bg-amber-100 px-1 rounded">{report.fixVersion}</code> isn't a version on project{' '}
                  <code className="bg-amber-100 px-1 rounded">{report.projectKey}</code>. Check for a typo — an unknown
                  version matches no stories, which looks the same as an empty release.
                </p>
              </div>
            </div>
          )}

          {/* Independent partial-failure error cards (Jira / GitHub) */}
          {backendErrors?.jira && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-red-800">Jira request failed</p>
                <p className="text-sm text-red-600 mt-1">{backendErrors.jira}</p>
              </div>
            </div>
          )}
          {backendErrors?.github && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-red-800">GitHub request failed</p>
                <p className="text-sm text-red-600 mt-1">{backendErrors.github}</p>
              </div>
            </div>
          )}
          {backendErrors?.confluence && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-red-800">Confluence request failed</p>
                <p className="text-sm text-red-600 mt-1">{backendErrors.confluence}</p>
              </div>
            </div>
          )}

          {/* Summary stat strip */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {STATES.map((state) => (
              <StatTile
                key={state.key}
                state={state}
                value={counts[state.key] || 0}
                active={activeFilter === state.key}
                onClick={() => setActiveFilter(activeFilter === state.key ? 'all' : state.key)}
              />
            ))}
          </div>

          {/* Cherry-pick workflow callout */}
          {showCherryPickCallout && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
              <Info className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-amber-800">
                Stories are cherry-picked from <code className="bg-amber-100 px-1 rounded">develop</code> into the release branch.
                Stories merged to develop but not yet cherry-picked appear as <strong>“Merged to develop only”</strong>.
              </p>
            </div>
          )}

          {/* Orphan note — the rows themselves now live in the table below, tagged and flagged */}
          {report.orphanCount > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-800">
                {report.orphanCount} row{report.orphanCount === 1 ? '' : 's'} below {report.orphanCount === 1 ? "doesn't" : "don't"} match any story in this fix version —{' '}
                merged/open PRs into <code className="bg-red-100 px-1 rounded">{report.releaseBranch}</code> whose Jira story has no/wrong Fix Version, a typo'd key, or no key at all. Look for the <span className="font-medium">Orphan</span> tag.
              </p>
            </div>
          )}

          {/* Carried-forward note — explains the teal "Carried forward" tagged rows */}
          {report.carriedForwardCount > 0 && (
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 flex items-start gap-3">
              <GitBranch className="w-5 h-5 text-teal-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-teal-800">
                {report.carriedForwardCount} row{report.carriedForwardCount === 1 ? '' : 's'} below {report.carriedForwardCount === 1 ? 'is' : 'are'} carried forward from an earlier release —{' '}
                already tagged to an earlier Fix Version and deliberately merged into <code className="bg-teal-100 px-1 rounded">{report.releaseBranch}</code> to avoid orphaning them on the earlier branch. They're informational, not gaps. Look for the <span className="font-medium">Carried forward</span> tag.
              </p>
            </div>
          )}

          {/* Sprint inclusion note — explains the purple "Sprint" tagged rows */}
          {report.sprintId && report.sprintStoryCount > 0 && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 flex items-start gap-3">
              <Layers className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-purple-800">
                {report.sprintStoryCount} row{report.sprintStoryCount === 1 ? '' : 's'} below {report.sprintStoryCount === 1 ? 'is' : 'are'} on the selected sprint but not tagged with fix version{' '}
                <code className="bg-purple-100 px-1 rounded">{report.fixVersion}</code>. Look for the <span className="font-medium">Sprint</span> tag. These are shown for visibility and are not published to the Confluence release page.
              </p>
            </div>
          )}

          {/* Filter pills */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setActiveFilter('all')}
              className={`px-3 py-1 text-xs rounded-full transition-colors ${
                activeFilter === 'all' ? 'bg-amber-400 text-gray-900' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All ({stories.length})
            </button>
            {STATES.map((state) => (
              <button
                key={state.key}
                onClick={() => setActiveFilter(activeFilter === state.key ? 'all' : state.key)}
                className={`px-3 py-1 text-xs rounded-full transition-colors ${
                  activeFilter === state.key ? 'bg-amber-400 text-gray-900' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {state.label} ({counts[state.key] || 0})
              </button>
            ))}
          </div>

          {/* Story table */}
          {stories.length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 p-10 text-center text-gray-500">
              <p className="font-medium text-gray-600">No stories found for this fix version.</p>
              <p className="text-sm mt-1">Check the fix version name and project.</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              <div className="overflow-x-auto max-h-[65vh]">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-gray-50 border-b border-gray-200">
                      {COLUMNS.map((col) => {
                        const active = sort.column === col.key;
                        const SortIcon = active ? (sort.direction === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
                        return (
                          <th
                            key={col.key}
                            onClick={() => toggleSort(col.key)}
                            className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap cursor-pointer select-none hover:bg-gray-100"
                          >
                            <span className="inline-flex items-center gap-1">
                              {col.label}
                              <SortIcon className={`w-3 h-3 ${active ? 'text-amber-500' : 'text-gray-300'}`} />
                            </span>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStories.map((story, idx) => (
                      <tr
                        key={story.rowId || story.key}
                        className={`border-b border-gray-100 ${
                          story.isOrphan ? 'bg-red-100/60' : ROW_TINT[story.state] || (idx % 2 === 1 ? 'bg-gray-50/50' : '')
                        }`}
                      >
                        <td className="px-4 py-2.5 whitespace-nowrap">
                          {story.keyIsJira ? (
                            <a
                              href={`${JIRA_BASE}/${story.key}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-amber-600 hover:text-amber-700 font-medium inline-flex items-center gap-1"
                            >
                              {story.key}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          ) : (
                            <a
                              href={story.releasePRs?.[0]?.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-gray-600 hover:text-gray-800 font-medium inline-flex items-center gap-1"
                              title="No Jira story to link to — showing the GitHub PR instead"
                            >
                              {story.key}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                          {story.isCrossProject && report.includeLabel && (
                            <span
                              className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-indigo-100 text-indigo-700 align-middle"
                              title={`Included via "${report.includeLabel}" label (${story.project} project)`}
                            >
                              <Tag className="w-2.5 h-2.5" />
                              {report.includeLabel}
                            </span>
                          )}
                          <SprintBadge story={story} />
                          <CarriedForwardBadge story={story} />
                          <OrphanBadge story={story} />
                          <ExceptionBadge story={story} />
                        </td>
                        <td className="px-4 py-2.5 max-w-md truncate text-gray-700" title={story.summary}>
                          {story.summary}
                        </td>
                        <td className="px-4 py-2.5 whitespace-nowrap text-gray-600">
                          {story.repos?.length ? (
                            <span className="text-xs text-gray-500">{story.repos.join(', ')}</span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="px-4 py-2.5 whitespace-nowrap text-gray-600">{story.assignee || '—'}</td>
                        <td className="px-4 py-2.5 whitespace-nowrap">
                          {story.missingDevelopPr ? (
                            <span
                              className="inline-flex items-center gap-1 text-xs text-amber-700 font-medium"
                              title="This merged/opened straight into the release branch with no develop PR — confirm it's an intentional hotfix, not a skipped cherry-pick."
                            >
                              <AlertCircle className="w-3 h-3 flex-shrink-0" />
                              No develop PR
                            </span>
                          ) : (
                            <PRBadgeList prs={story.developPRs} />
                          )}
                        </td>
                        <td className="px-4 py-2.5 whitespace-nowrap">
                          <PRBadgeList prs={story.releasePRs} />
                        </td>
                        <td className="px-4 py-2.5 whitespace-nowrap text-gray-600">{formatDate(story.mergedAt)}</td>
                        <td className="px-4 py-2.5 whitespace-nowrap">
                          <StatePill state={story.state} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {report.generatedAt && (
            <p className="text-xs text-gray-400 text-right">
              Generated {new Date(report.generatedAt).toLocaleString()}
              {report.includeLabel ? ` · incl. "${report.includeLabel}"-labeled stories from other projects` : ''}
              {report.sprintId ? ` · incl. ${report.sprintStoryCount} sprint stor${report.sprintStoryCount === 1 ? 'y' : 'ies'} not in this fix version` : ''}
              {report.repos?.length ? ` · ${report.repos.map((r) => r.split('/').pop()).join(', ')}` : ''}
              {report.confluence?.publishedAt ? ` · Published to Confluence ${formatDateTime(report.confluence.publishedAt)}` : ''}
            </p>
          )}
        </>
      )}
    </div>
  );
}
